import React, { useState } from 'react'
import Header from '../../../components/navigation/Header'
import { Box, Button, Chip, IconButton, Tooltip, Typography, useMediaQuery, Divider, Checkbox } from '@mui/material'
import { makeStyles } from '@mui/styles';
import KenTable from '../../../components/KenTable';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";

const useStyles = makeStyles(() => ({
    header: {
        fontSize: '18px',
        fontWeight: '700',
    },
    container: {
        borderRadius: '8px',
        background: '#ffffff',
        marginTop: '32px',
        minHeight: 'calc(100vh - 165px)',
        position: 'relative',
        padding: '24px'
    },
    tabRoot: {
        textTransform: 'capitalize', // 👈 make labels capitalized
        fontSize: '16px !important',
        fontWeight: 500,
    },
    footerAmount: {
        border: '1px solid #373A45',
        background: '#EAEFFF',
        borderRadius: '8px',
        padding: '0 16px',
        height: 36,
        display: 'flex',
        alignItems: 'center'
    }
}));



const Enrollment = () => {
    const classes = useStyles();
    const [totalAmount, setTotalAmount] = useState(0);
    const [open, setOpen] = useState(false);
    const isMobile = useMediaQuery('(max-width:600px)');
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const handleSubmit = () => {
        console.log("Enrollment confirmed!");
        setOpen(false);
    };


    const handleSelectionChange = (selectedIds: number[]) => {
        setSelectedIds(selectedIds);
        setTotalAmount(
            rows
                .filter((r) => selectedIds.includes(r.id))
                .reduce((sum, r) => sum + (r.amount || 0), 0)
        );
    };

    const columns = [
        { field: "courseName", headerName: "Course name" },
        { field: "courseCode", headerName: "Course code" },
        { field: "exam", headerName: "Semester" },
        { field: "credits", headerName: "Credits" },
        { field: "format", headerName: "Format" },
        { field: "type", headerName: "Type" },
        {
            field: "eligibility",
            headerName: "Eligibility",
            renderCell: ({ value }: any) =>
                value === "Eligible" ? (
                    <Chip label="Eligible" sx={{ borderRadius: "16px", background: "#C1DCC3", color: "#1F4E21" }} />
                ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Chip
                            label="Not eligible"
                            sx={{ borderRadius: "16px", background: "#F1BFBF", color: "#591414" }}
                        />
                        <Tooltip slotProps={{
                            popper: {
                                sx: {
                                    "& .MuiTooltip-tooltip": {
                                        bgcolor: "white",
                                        color: "black",
                                        fontSize: "0.85rem",
                                    },
                                    "& .MuiTooltip-arrow": {
                                        color: "white", // arrow matches background
                                    },
                                },
                            },
                        }}
                            title="You are not eligible for this exam as your attendance is below 40%. Please contact your mentor for further guidance."
                            arrow
                        >
                            <IconButton size="small">
                                <InfoOutlineIcon fontSize="small" color="error" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                ),
        },
    ];

    const rows = [
        {
            id: 1,
            courseName: "Network Analysis & Synthesis",
            courseCode: "ECE101",
            exam: "End Semester",
            credits: 3,
            format: "Theory",
            type: "Regular",
            eligibility: "Eligible",
            amount: 2000
        },
        {
            id: 2,
            courseName: "Signals & Systems",
            courseCode: "ECE102",
            exam: "End Semester",
            credits: 4,
            format: "Theory",
            type: "Backlog",
            eligibility: "Not eligible",
        },
        {
            id: 3,
            courseName: "Network Analysis & Synthesis",
            courseCode: "ECE101",
            exam: "End Semester",
            credits: 3,
            format: "Theory",
            type: "Regular",
            eligibility: "Eligible",
            amount: 1000
        },
        {
            id: 4,
            courseName: "Network Analysis & Synthesis",
            courseCode: "ECE101",
            exam: "End Semester",
            credits: 3,
            format: "Theory",
            type: "Regular",
            eligibility: "Eligible",
            amount: 0
        },
    ];

    const toggleCourse = (rowId: number, eligible: boolean) => {
        if (!eligible) {
            return;
        }
        handleSelectionChange(
            selectedIds.includes(rowId)
                ? selectedIds.filter((id) => id !== rowId)
                : [...selectedIds, rowId]
        );
    };

    const renderMobileCards = () => (
        <Box mt={3} display="flex" flexDirection="column" gap={2} pb={12}>
            <Typography className={classes.header}>Semester I</Typography>
            <Box
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderRadius: "999px",
                    backgroundColor: "#D32F2F",
                    color: "white",
                    fontWeight: 600,
                    fontSize: 13,
                    width: "fit-content",
                }}
            >
                Window closes in <Typography component="span" fontWeight={700}>12:00 minutes</Typography>
            </Box>

            {rows.map((row) => {
                const isSelected = selectedIds.includes(row.id);
                const isEligible = row.eligibility === "Eligible";
                return (
                    <Box
                        key={row.id}
                        onClick={() => toggleCourse(row.id, isEligible)}
                        sx={{
                            borderRadius: "18px",
                            padding: 2,
                            background: isEligible ? "#F6F8FB" : "#FBEAEA",
                            position: "relative",
                            border: `1px solid ${isSelected ? "#1A1A2E" : "transparent"}`,
                            boxShadow: isSelected ? "0 8px 20px rgba(17, 24, 39, 0.15)" : "none",
                        }}
                    >
                        <Box display="flex" alignItems="flex-start" gap={1.5}>
                            <Checkbox
                                checked={isSelected}
                                disabled={!isEligible}
                                onChange={() => toggleCourse(row.id, isEligible)}
                                sx={{ p: 0.5 }}
                            />
                            <Box flex={1}>
                                <Typography fontWeight={700} fontSize={16}>
                                    {row.courseName}
                                </Typography>
                                <Typography fontSize={13} color="text.secondary" mt={0.5}>
                                    {row.courseCode} | {row.credits} Credits
                                </Typography>
                                <Divider sx={{ my: 1.5 }} />
                                <Box display="flex" flexDirection="column" gap={0.75}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Type
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.type}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Format
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.format}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={13} color="text.secondary">
                                            Exam
                                        </Typography>
                                        <Typography fontSize={13} fontWeight={500}>
                                            {row.exam}
                                        </Typography>
                                    </Box>
                                </Box>
                                {!isEligible && (
                                    <Box
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 1,
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: "999px",
                                            backgroundColor: "#D14343",
                                            color: "white",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            mt: 2,
                                        }}
                                    >
                                        Not eligible
                                    </Box>
                                )}
                            </Box>
                        </Box>
                        {/* {!isEligible && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                    transform: "translate(-50%, -50%)",
                                    backgroundColor: "white",
                                    borderRadius: "12px",
                                    boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.22)",
                                    padding: 2,
                                    width: "90%",
                                }}
                            >
                                <Typography fontSize={13} color="text.secondary">
                                    You are not eligible for this exam as your attendance is below 40%. Please contact your mentor for further guidance.
                                </Typography>
                            </Box>
                        )} */}
                    </Box>
                );
            })}
        </Box>
    );

    return (
        <Box sx={{ position: "relative", pb: isMobile ? "116px" : 0 }}>
            <Header title="Enroll" />

            <Box className={classes.container}>
                {isMobile ? (
                    <>
                        {renderMobileCards()}
                    </>
                ) : (
                    <>
                        <Box className="d-flex justify-content-between align-items-center">
                            <Typography className={classes.header}>Semester I</Typography>
                            <Chip label="Window closes in 12:00 minutes" sx={{ borderRadius: "16px", background: "#D32F2F", color: "white" }} />
                        </Box>
                        <Box className="mt-4">
                            <KenTable
                                columns={columns}
                                rows={rows}
                                showCheckbox
                                onSelectionChange={handleSelectionChange}
                                footer={
                                    <Box display="flex" justifyContent="end" alignItems="center" gap={4}>
                                        <Box className={classes.footerAmount}>
                                            Total fee: &nbsp; <span className="fw-bold">₹{totalAmount}</span>
                                        </Box>
                                        <Button variant="secondary" sx={{ color: 'white !important' }} disabled={totalAmount === 0} onClick={handleOpen}>
                                            Proceed
                                        </Button>
                                    </Box>
                                }
                            />
                        </Box>
                    </>
                )}
            </Box>
            {isMobile && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: 65,
                        left: 0,
                        right: 0,
                        px: 2,
                    }}
                >
                    <Box
                        sx={{
                            maxWidth: 480,
                            margin: "0 auto",
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: "16px",
                                backgroundColor: "#FFFFFF",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.18)",
                                padding: "12px",
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1.5,
                                    borderRadius: "12px",
                                    padding: "12px",
                                    border: "1px solid #CBD5E1",
                                    backgroundColor: "#EFF3FF",
                                }}
                            >
                                <Typography fontWeight={600}>Total fee:</Typography>
                                <Typography fontWeight={700}>₹{totalAmount.toLocaleString()}</Typography>
                            </Box>
                            <Button
                                variant="secondary"
                                fullWidth
                                disabled={totalAmount === 0}
                                onClick={handleOpen}
                                sx={{
                                    color: "white !important",
                                    borderRadius: "12px",
                                    py: 1.5,
                                    fontWeight: 600,
                                }}
                            >
                                Proceed
                            </Button>
                        </Box>
                    </Box>
                </Box>
            )}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        padding: "16px",
                    },
                }}
            >
                <DialogTitle sx={{ fontSize: "18px", fontWeight: 700, pb: 1 }}>
                    Confirm Enrollment?
                </DialogTitle>

                <DialogContent>
                    <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                        Once you enroll for these exams, you won’t be able to add or switch
                        courses. Please review your selections carefully before submitting.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "end", px: 2, pb: 2 }}>
                    <Button
                        onClick={handleClose}
                        sx={{ textTransform: "none", color: "text.secondary" }}
                    >
                        Review selection
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default Enrollment
